import FRAGMENT_SHADER from "./shaders/mesh-custom-pbr.fs";
import VERTEX_SHADER from "./shaders/mesh-custom-pbr.vs";
import Utils from "../utils";

class MeshCustomPBRMaterial extends THREE.ShaderMaterial {
    constructor(parameters) {
        const base = THREE.ShaderLib.standard;

        const uniforms = THREE.UniformsUtils.merge([base.uniforms, {
            smoothClipEnd: new THREE.Uniform(1),
            smoothClipStart: new THREE.Uniform(0.1),

            refractiveIndex: new THREE.Uniform(1.02),
            fresnelBias: new THREE.Uniform(0.1),
            fresnelScale: new THREE.Uniform(2.0),
            fresnelPower: new THREE.Uniform(1.0),
            refractionRed: new THREE.Uniform(1.0),
            refractionGreen: new THREE.Uniform(0.99),
            refractionBlue: new THREE.Uniform(0.98),

            secondaryColor: new THREE.Uniform(new THREE.Color(0xffffff)),

            envMapCubeRes: new THREE.Uniform(1),
        }]);

        if (Utils.queryParams.has("debug_uniforms")) {
            Object.keys(uniforms).forEach(uname => {
                const u = uniforms[uname];
                let oldUniform = u.value;
                delete u.value;
                Object.defineProperty(u, "value", {
                    get: () => oldUniform,
                    set: val => {
                        if (val === undefined) {
                            console.warn(`Trying to set invalid uniform '${uname}' for material.`);
                            return;
                        }
                        oldUniform = val;
                    }
                });
            });
        }

        const defines = {
            STANDARD: ""
        };

        if (parameters && parameters.cubemapRendersWithSeams) {
            defines.CUBEMAP_RENDERS_WITH_SEAMS = "";
        }

        super({
            uniforms,
            defines,
            fragmentShader: FRAGMENT_SHADER,
            vertexShader: VERTEX_SHADER,
            wireframe: true
        });

        this._glassMode = false;

        this.materialType = "pbr";

        this.color = new THREE.Color(0xffffff); // diffuse
        this.roughness = 1;
        this.metalness = 0;
        this.reflectivity = 1;

        this.map = null;
        // let _map = null;

        // Object.defineProperty(this, "map", {
        //     get: () => _map,
        //     set: v => {
        //         debugger;

        //         _map = v;
        //     }
        // });

        this.lightMap = null;
        this.lightMapIntensity = 1.0;

        this.aoMap = null;
        this.aoMapIntensity = 1.0;

        this.emissive = new THREE.Color(0x000000);
        this.emissiveIntensity = 1.0;
        this.emissiveMap = null;

        this.bumpMap = null;
        this.bumpScale = 1;

        this.normalMap = null;
        this.normalScale = new THREE.Vector2(1, 1);

        this.displacementMap = null;
        this.displacementScale = 1;
        this.displacementBias = 0;

        this.roughnessMap = null;


        this.metalnessMap = null;

        this.alphaMap = null;

        this.envMap = null;
        this.envMapIntensity = 1.0;

        this.refractionRatio = 0.98;

        this.wireframe = false;
        this.wireframeLinewidth = 1;
        this.wireframeLinecap = "round";
        this.wireframeLinejoin = "round";

        this.skinning = false;
        this.morphTargets = false;
        this.morphNormals = false;

        this.smoothClipEnd = uniforms.smoothClipEnd;
        this.smoothClipStart = uniforms.smoothClipStart;
        this._smoothClipEnabled = false;

        this.setValues(parameters);

        this.isMeshStandardMaterial = true;
        this.lights = true;
        this.fog = true;

        this.clippingPlanes = [];
    }

    copy(source) {
        THREE.ShaderMaterial.prototype.copy.call(this, source);
        this.color.copy(source.color);
        this.roughness = source.roughness;
        this.metalness = source.metalness;

        this.map = source.map;

        this.lightMap = source.lightMap;
        this.lightMapIntensity = source.lightMapIntensity;

        this.aoMap = source.aoMap;
        this.aoMapIntensity = source.aoMapIntensity;

        this.emissive.copy(source.emissive);
        this.emissiveIntensity = source.emissiveIntensity;
        this.emissiveMap = source.emissiveMap;

        this.bumpMap = source.bumpMap;
        this.bumpScale = source.bumpScale;

        this.normalMap = source.normalMap;
        this.normalScale.copy(source.normalScale);

        this.displacementMap = source.displacementMap;
        this.displacementScale = source.displacementScale;
        this.displacementBias = source.displacementBias;

        this.roughnessMap = source.roughnessMap;

        this.metalnessMap = source.metalnessMap;

        this.alphaMap = source.alphaMap;

        this.envMap = source.envMap;
        this.envMapIntensity = source.envMapIntensity;

        this.refractionRatio = source.refractionRatio;

        this.wireframe = source.wireframe;
        this.wireframeLinewidth = source.wireframeLinewidth;
        this.wireframeLinecap = source.wireframeLinecap;
        this.wireframeLinejoin = source.wireframeLinejoin;

        this.skinning = source.skinning;
        this.morphTargets = source.morphTargets;
        this.morphNormals = source.morphNormals;

        this.smoothClipEnd = source.smoothClipEnd;
        this.smoothClipStart = source.smoothClipStart;
        this._smoothClipEnabled = source._smoothClipEnabled;

        this.isMeshStandardMaterial = source.isMeshStandardMaterial;
        this.lights = source.lights
        this.fog = source.fog;

        this.envMapCubeRes = source.envMapCubeRes;

        return this;
    }

    _catchParams() {
        // const mData = Q3.data.assets.materials[this.uuid];

        this.needsUpdate = true;
    }

    setSmoothClipEnabled(enabled) {
        this._smoothClipEnabled = enabled;

        if (enabled) this.defines["SMOOTH_CLIP"] = "";
        else delete this.defines["SMOOTH_CLIP"];

        this.needsUpdate = true;
    }

    get secondaryColor() { return this.uniforms.secondaryColor.value; }
    set secondaryColor(value) {
        this.uniforms.secondaryColor.value = value;
    }

    get isSmoothClipEnabled() { return this._smoothClipEnabled; }

    get transparent() { return this._transparent; }
    set transparent(value) {
        this._transparent = value;
        if (!!this.defines) { //happens only in material constructor which sets it to false
            if (value) this.defines["TRANSPARENT"] = "";
            else delete this.defines["TRANSPARENT"];
            this.needsUpdate = true;
        }
    }

    get glassMode() { return this._glassMode; }
    set glassMode(value) {
        if (Q3.browserCapabilities.supports.glassMode.enabled) {
            this._glassMode = value;
            if (!!this.defines) { //happens only in material constructor which sets it to false
                if (value) this.defines["GLASS_MODE"] = "";
                else delete this.defines["GLASS_MODE"];
                this.needsUpdate = true;
            }
        }
    }

    get refractiveIndex() { return this.uniforms.refractiveIndex.value; }
    set refractiveIndex(v) { this.uniforms.refractiveIndex.value = v; }
    get refractionRed() { return this.uniforms.refractionRed.value; }
    set refractionRed(v) { this.uniforms.refractionRed.value = v; }
    get refractionBlue() { return this.uniforms.refractionBlue.value; }
    set refractionBlue(v) { this.uniforms.refractionBlue.value = v; }
    get refractionGreen() { return this.uniforms.refractionGreen.value; }
    set refractionGreen(v) { this.uniforms.refractionGreen.value = v; }
    get fresnelBias() { return this.uniforms.fresnelBias.value; }
    set fresnelBias(v) { this.uniforms.fresnelBias.value = v; }
    get fresnelScale() { return this.uniforms.fresnelScale.value; }
    set fresnelScale(v) { this.uniforms.fresnelScale.value = v; }
    get fresnelPower() { return this.uniforms.fresnelPower.value; }
    set fresnelPower(v) { this.uniforms.fresnelPower.value = v; }

    get envMapCubeRes() { return this.uniforms.envMapCubeRes.value; }
    set envMapCubeRes(v) { this.uniforms.envMapCubeRes.value = v; }
    set cubemapRendersWithSeams(v) {
        v = Boolean(v);
        if (v === ('CUBEMAP_RENDERS_WITH_SEAMS' in this.defines)) {
            return;
        }

        if (v) {
            this.defines.CUBEMAP_RENDERS_WITH_SEAMS = true;
        } else {
            delete this.defines.CUBEMAP_RENDERS_WITH_SEAMS;
        }

        this.needsUpdate = true;
    }
    get cubemapRendersWithSeams() {
        return 'CUBEMAP_RENDERS_WITH_SEAMS' in this.defines;
    }

}

export default MeshCustomPBRMaterial;
export { MeshCustomPBRMaterial };